import { SearchResultRow } from '@/features/header/components/SearchResultRow';
import { MIN_QUERY_LENGTH } from '@/features/header/consts/search';
import { useMergedSearchResults } from '@/features/header/hooks/useMergedSearchResults';
import type { SearchRow, SearchSection } from '@/features/header/types/searchResults';
import { useTranslation } from 'react-i18next';

interface SearchResultsProps {
  query: string;
  onSelect: () => void;
}

const getRowKey = (row: SearchRow): string => (row.kind === 'nav' ? `nav-${row.to}` : `${row.entityType}-${row.id}`);

const centeredMessageClass = 'text-md flex h-10 items-center justify-center p-3 text-center font-semibold';

export const SearchResults = ({ query, onSelect }: SearchResultsProps) => {
  const { t } = useTranslation();
  const { sections, isFetching, isError, isEmpty } = useMergedSearchResults(query);

  const trimmedQuery = query.trim();

  if (trimmedQuery.length < MIN_QUERY_LENGTH) {
    return <div className={centeredMessageClass}>{t('label.emptyQuery')}</div>;
  }

  if (isError) {
    return <div className={centeredMessageClass}>{t('header.search.error')}</div>;
  }

  if (isEmpty) {
    if (isFetching) {
      return <div className={centeredMessageClass}>{t('header.search.loading')}</div>;
    }
    return <div className={centeredMessageClass}>{t('label.nothingFound')}</div>;
  }

  const renderSection = (section: SearchSection) => {
    const headerLabel = section.labelKey ? t(section.labelKey) : section.fallbackLabel;

    return (
      <li key={section.id}>
        <div className="px-4 pt-2 pb-1 text-xs font-semibold text-neutral-400 uppercase">{headerLabel}</div>
        <ul>
          {section.rows.map((row) => (
            <SearchResultRow key={getRowKey(row)} row={row} onSelect={onSelect} />
          ))}
        </ul>
      </li>
    );
  };

  return (
    <ul className="scrollbar-none max-h-80 overflow-y-auto rounded-lg bg-white py-1">{sections.map(renderSection)}</ul>
  );
};
